import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus } from 'lucide-react';

interface TagInputProps {
  value?: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  className?: string;
}

export const TagInput: React.FC<TagInputProps> = ({
  value = '',
  onChange,
  options,
  placeholder = 'Vyberte nebo napište...',
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter((opt) => opt.toLowerCase().includes(filter.toLowerCase()));

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
    setFilter('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        <div
          className="relative w-full"
          onClick={() => {
            setIsOpen(true);
            inputRef.current?.focus();
          }}
        >
          {/* Display as a tag if value is set, or input if editing */}
          <div className="flex items-center w-full p-2.5 rounded border dark:bg-zinc-800 dark:border-zinc-700 bg-white">
            <input
              ref={inputRef}
              className="w-full bg-transparent outline-none text-sm"
              placeholder={value ? value : placeholder}
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setIsOpen(true);
                // If user types, we can treat it as changing value directly or wait for enter
                // But simpler is to allow direct typing
                if (e.target.value) onChange(e.target.value);
              }}
              onFocus={() => setIsOpen(true)}
            />
            <ChevronDown size={14} className="text-gray-400 absolute right-3 pointer-events-none" />
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-1 bg-white dark:bg-zinc-800 border dark:border-zinc-700 rounded-md shadow-lg max-h-60 overflow-y-auto z-50">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-zinc-700"
                onClick={() => handleSelect(opt)}
              >
                {opt}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-xs text-gray-400 italic">Žádné shody</div>
          )}

          {filter && !filteredOptions.includes(filter) && filter !== value && (
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 border-t dark:border-zinc-700 flex items-center gap-2"
              onClick={() => handleSelect(filter)}
            >
              <Plus size={14} /> Použít "{filter}"
            </button>
          )}
        </div>
      )}
    </div>
  );
};
